import { z } from 'zod';

/** Internal database identifier. Serialised as a JSON number. */
export const IdSchema = z.coerce.number().int().positive();
export type Id = z.infer<typeof IdSchema>;

/** An identifier accepted from a URL path segment. */
export const IdParamSchema = z.object({ id: IdSchema });

/** ISO-8601 instant, always stored and transmitted in UTC. */
export const InstantSchema = z.string().datetime({ offset: true });
export type Instant = z.infer<typeof InstantSchema>;

/** Calendar date with no time component, `YYYY-MM-DD`. */
export const DateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
export type DateOnly = z.infer<typeof DateOnlySchema>;

/** Wall-clock time of day, `HH:MM`, 24-hour. */
export const TimeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Expected HH:MM');
export type TimeOfDay = z.infer<typeof TimeOfDaySchema>;

/** IANA timezone name, e.g. `Europe/London`. Validated against the runtime ICU database. */
export const TimezoneSchema = z.string().min(1).refine(
  (tz) => {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  },
  { message: 'Unknown IANA timezone' },
);

/** Case-insensitive email address, normalised to lowercase. */
export const EmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(320)
  .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email address');

/**
 * A machine-readable key used for enum values, custom-field keys and permission
 * names. Lowercase snake_case keeps keys safe to embed in JSON paths and SQL.
 */
export const SlugSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9_]*$/, 'Must be lowercase snake_case starting with a letter');

/** Free-form label shown to people. */
export const LabelSchema = z.string().trim().min(1).max(255);

/** Arbitrary JSON accepted from clients. */
export type JsonValue = string | number | boolean | null | JsonValue[] | { [k: string]: JsonValue };
export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(z.string(), JsonValueSchema),
  ]),
);

export const CustomFieldsSchema = z.record(z.string(), JsonValueSchema);
export type CustomFields = z.infer<typeof CustomFieldsSchema>;

/**
 * Shared shapes kept beside these, carried to the package root by this
 * module's place in the index (see `../index.ts`). A module re-exported from
 * here must not import from it in turn: see `notifications.ts`.
 *
 * - notifications: the notification shape the API returns and the bell reads.
 * - people: agent types and requester groups.
 */
export * from './notifications.js';
export * from './people.js';
